import React, { useState, useMemo, type ReactNode } from "react";
import {
  type FormatMetricOptions,
  type MetricFormatterFunction,
  type DataPoint,
  type ChartThreshold,
  type LegendPosition,
  type LegendSummaryMode,
  type LegendInteraction,
  type MissingBucketValue,
  type ChartProps,
} from "@/src/features/widgets/chart-library/chart-props";
import { formatMetric } from "@/src/features/widgets/chart-library/utils";
import { isChartDataEmpty } from "@/src/features/widgets/chart-library/isChartDataEmpty";
import { NoDataOrLoading } from "@/src/components/NoDataOrLoading";
import { CardContent } from "@/src/components/ui/card";
import { LineChartTimeSeries } from "@/src/features/widgets/chart-library/LineChartTimeSeries";
import { AreaChartTimeSeries } from "@/src/features/widgets/chart-library/AreaChartTimeSeries";
import { VerticalBarChartTimeSeries } from "@/src/features/widgets/chart-library/VerticalBarChartTimeSeries";
import { TopListChart } from "@/src/features/widgets/chart-library/TopListChart";
import { VerticalBarChart } from "@/src/features/widgets/chart-library/VerticalBarChart";
import { PieChart } from "@/src/features/widgets/chart-library/PieChart";
import HistogramChart from "@/src/features/widgets/chart-library/HistogramChart";
import { type DashboardWidgetChartType } from "@langfuse/shared/src/db";
import { Button } from "@/src/components/ui/button";
import { AlertCircle } from "@langfuse/icons";
import { BigNumber } from "@/src/features/widgets/chart-library/BigNumber";
import { PivotTable } from "@/src/features/widgets/chart-library/PivotTable";
import { type OrderByState } from "@langfuse/shared";

const DEFAULT_METRIC_THEME = {
  light: "hsl(var(--chart-1))",
  dark: "hsl(var(--chart-1))",
} as const;

/**
 * Chart types whose recharts primitive draws nothing but empty axes/grid when
 * every point is null/zero — a blank canvas rather than guidance (manifesto
 * principle 8). Decided once here so no time-series component re-derives the
 * emptiness check. (LFE-14333)
 */
const EMPTY_STATE_CHART_TYPES = new Set<DashboardWidgetChartType>([
  "LINE_TIME_SERIES",
  "AREA_TIME_SERIES",
  "BAR_TIME_SERIES",
  "NUMBER",
]);

const ChartComponent = ({
  chartType,
  data,
  rowLimit,
  chartConfig,
  config,
  sortState,
  onSortChange,
  isLoading = false,
  legendPosition,
  legendSummary,
  legendInteraction,
  maxVisibleSeries,
  syncId,
  sync,
  overrideWarning = false,
  metricFormatter: metricFormatterOverride,
  thresholds,
  missingValue,
  hideXAxisLabels,
  compactYAxis,
  colorBarsByCategory,
  zeroBaseline,
  emptyState,
}: {
  chartType: DashboardWidgetChartType;
  data: DataPoint[];
  rowLimit: number;
  chartConfig?: {
    type: DashboardWidgetChartType;
    row_limit?: number;
    bins?: number;
    dimensions?: string[];
    metrics?: string[];
    units?: (string | undefined)[];
    unit?: string | undefined;
    defaultSort?: OrderByState;
    show_value_labels?: boolean;
    show_data_point_dots?: boolean;
  };
  config?: ChartProps["config"];
  sortState?: OrderByState | null;
  onSortChange?: (sortState: OrderByState | null) => void;
  isLoading?: boolean;
  legendPosition?: LegendPosition;
  legendSummary?: LegendSummaryMode;
  legendInteraction?: LegendInteraction;
  maxVisibleSeries?: number;
  syncId?: string;
  sync?: ChartProps["sync"];
  overrideWarning?: boolean;
  metricFormatter?: MetricFormatterFunction;
  thresholds?: ChartThreshold[];
  /** See {@link MissingBucketValue}; consumed by line/area time series. */
  missingValue?: MissingBucketValue;
  /**
   * Hide x-axis tick labels on a categorical (entity-name) axis; the full name
   * stays in the hover tooltip. Off by default. Consumed by the time-series
   * charts and forwarded to `prepareTimeAxis`. Used by dataset-compare charts.
   */
  hideXAxisLabels?: boolean;
  /** See {@link ChartProps.compactYAxis}. Consumed by LINE_TIME_SERIES. */
  compactYAxis?: boolean;
  /**
   * Colour each bar of a categorical axis and name it in a legend below the
   * plot; see {@link ChartProps.colorBarsByCategory}. Consumed by VERTICAL_BAR.
   */
  colorBarsByCategory?: boolean;
  /** See {@link ChartProps.zeroBaseline}. Consumed by VERTICAL_BAR. */
  zeroBaseline?: boolean;
  /**
   * Replaces the default "No data" card when the chart has nothing to draw.
   * For a chart in a band too short for that card (the table strips), where it
   * would clip its own text. Pass a stable node so the memo still holds.
   */
  emptyState?: ReactNode;
}) => {
  const [forceRender, setForceRender] = useState(overrideWarning);
  const shouldWarn = data.length > 2000 && !forceRender;

  const metricFormatter = useMemo(
    () =>
      metricFormatterOverride ??
      ((value: number, options: FormatMetricOptions) =>
        formatMetric(value, { ...options, unit: chartConfig?.unit })),
    [metricFormatterOverride, chartConfig?.unit],
  );

  // Time-axis formatting is NOT decided here. Raw time_dimension values flow
  // straight to the visualiser, which formats them via the prepareTimeAxis
  // preparer — one source of truth, so every chart formats time the same way.
  // (LFE-10549)
  const renderedData = data;

  const resolvedConfig = useMemo(() => {
    if (!config) return undefined;

    return Object.fromEntries(
      Object.entries(config).map(([key, value]) => {
        if (value.theme || value.color) {
          return [key, value];
        }

        return [
          key,
          {
            ...value,
            theme: DEFAULT_METRIC_THEME,
          },
        ];
      }),
    ) as NonNullable<ChartProps["config"]>;
  }, [config]);

  const renderChart = () => {
    // A time-series query can densify an empty range into null-filled bucket
    // rows rather than an empty array, so recharts still gets data — it just
    // draws blank axes with no series. Fail into guidance instead of that
    // blank box. A real 0 is never treated as empty here (see
    // isChartDataEmpty); skip while loading so a first paint doesn't flash
    // "No data" before the real result arrives. (LFE-14333, manifesto
    // principle 8)
    // A caller that supplied its own empty state (a table strip) gets it for
    // any chart type: its band is too short for the default card, whatever the
    // mark.
    if (
      (EMPTY_STATE_CHART_TYPES.has(chartType) || emptyState !== undefined) &&
      (chartType === "NUMBER" || !isLoading) &&
      isChartDataEmpty(data)
    ) {
      return emptyState ?? <NoDataOrLoading isLoading={isLoading} />;
    }

    switch (chartType) {
      case "LINE_TIME_SERIES":
        return (
          <LineChartTimeSeries
            data={renderedData}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
            legendPosition={legendPosition}
            legendSummary={legendSummary}
            legendInteraction={legendInteraction}
            maxVisibleSeries={maxVisibleSeries}
            syncId={syncId}
            sync={sync}
            showDataPointDots={chartConfig?.show_data_point_dots ?? false}
            thresholds={thresholds}
            missingValue={missingValue}
            hideXAxisLabels={hideXAxisLabels}
            compactYAxis={compactYAxis}
          />
        );
      case "AREA_TIME_SERIES":
        return (
          <AreaChartTimeSeries
            data={renderedData}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
            legendPosition={legendPosition}
            legendSummary={legendSummary}
            legendInteraction={legendInteraction}
            maxVisibleSeries={maxVisibleSeries}
            syncId={syncId}
            sync={sync}
            missingValue={missingValue}
            hideXAxisLabels={hideXAxisLabels}
          />
        );
      case "BAR_TIME_SERIES":
        return (
          <VerticalBarChartTimeSeries
            data={renderedData}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
            legendPosition={legendPosition}
            legendSummary={legendSummary}
            legendInteraction={legendInteraction}
            maxVisibleSeries={maxVisibleSeries}
            syncId={syncId}
            sync={sync}
            hideXAxisLabels={hideXAxisLabels}
          />
        );
      case "HORIZONTAL_BAR":
        return (
          <TopListChart
            data={renderedData.slice(0, rowLimit)}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
          />
        );
      case "VERTICAL_BAR":
        return (
          <VerticalBarChart
            data={renderedData.slice(0, rowLimit)}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
            hideXAxisLabels={hideXAxisLabels}
            colorBarsByCategory={colorBarsByCategory}
            legendPosition={legendPosition}
            zeroBaseline={zeroBaseline}
          />
        );
      case "PIE":
        return (
          <PieChart
            data={renderedData.slice(0, rowLimit)}
            metricFormatter={metricFormatter}
          />
        );
      case "HISTOGRAM":
        return (
          <HistogramChart
            data={renderedData}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
          />
        );
      case "NUMBER": {
        return (
          <BigNumber
            data={renderedData}
            config={resolvedConfig}
            metricFormatter={metricFormatter}
          />
        );
      }
      case "PIVOT_TABLE": {
        // Extract pivot table configuration from chartConfig
        const pivotConfig = {
          dimensions: chartConfig?.dimensions ?? [],
          metrics: chartConfig?.metrics ?? ["metric"], // Use metrics from chartConfig
          units: chartConfig?.units,
          rowLimit: chartConfig?.row_limit ?? rowLimit,
          defaultSort: chartConfig?.defaultSort,
        };
        return (
          <PivotTable
            data={renderedData}
            config={pivotConfig}
            sortState={sortState}
            onSortChange={onSortChange}
            isLoading={isLoading}
          />
        );
      }
      default:
        return (
          <TopListChart
            data={renderedData.slice(0, rowLimit)}
            metricFormatter={metricFormatter}
          />
        );
    }
  };

  const renderWarning = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <AlertCircle __rawClassName="mb-4 h-12 w-12" />
      <h3 className="mb-2 text-lg font-bold">Large Dataset Warning</h3>
      <p className="text-muted-foreground mb-6 text-sm">
        This chart has more than 2,000 unique data points. Rendering it may be
        slow or may crash your browser. Try to reduce the number of dimensions
        by adding more selective filters or choosing a coarser breakdown
        dimension.
      </p>
      <Button
        variant="outline"
        onClick={() => setForceRender(true)}
        className="font-bold"
      >
        I understand, proceed to render the chart
      </Button>
    </div>
  );

  return (
    <CardContent className="h-full p-0">
      {shouldWarn ? renderWarning() : renderChart()}
    </CardContent>
  );
};

/**
 * Memoized so a parent re-render (e.g. the dashboard query scheduler bumping its
 * version during load) doesn't reconcile the recharts subtree when the chart's
 * inputs are unchanged. Effective only when callers pass stable `data`/`config`
 * references — the dashboard time-series consumers memoize those. (LFE-10549)
 */
export const Chart = React.memo(ChartComponent);
